import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  LinearProgress,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  CloudUpload,
  Delete,
  Image as ImageIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    "&.dragover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
  },
  input: {
    display: "none",
  },
  preview: {
    position: "relative",
    marginTop: theme.spacing(2),
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: 300,
    borderRadius: theme.shape.borderRadius,
  },
  deleteButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: theme.palette.error.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  uploadIcon: {
    fontSize: 48,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  progress: {
    marginTop: theme.spacing(2),
  },
}));

const ImageUploader = ({
  currentImage,
  onUpload,
  onRemove,
  maxSizeMB = 5,
  acceptedFormats = ["image/jpeg", "image/png", "image/webp", "image/gif"],
}) => {
  const classes = useStyles();
  const [preview, setPreview] = useState(currentImage || null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const validateFile = (file) => {
    if (!acceptedFormats.includes(file.type)) {
      toast.error(
        `Formato no válido. Formatos aceptados: ${acceptedFormats
          .map((f) => f.split("/")[1])
          .join(", ")}`
      );
      return false;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      toast.error(`La imagen debe ser menor a ${maxSizeMB}MB`);
      return false;
    }

    return true;
  };

  const handleFileSelect = async (file) => {
    if (!validateFile(file)) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    setUploading(true);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 100);

    try {
      const imageUrl = await onUpload(file);
      setProgress(100);
      setPreview(imageUrl);
      toast.success("Imagen subida exitosamente");
    } catch (error) {
      toast.error("Error al subir la imagen");
      setPreview(null);
    } finally {
      clearInterval(progressInterval);
      setUploading(false);
      setProgress(0);
    }
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleRemove = () => {
    setPreview(null);
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <Box>
      {!preview ? (
        <Paper
          className={`${classes.root} ${dragOver ? "dragover" : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            accept={acceptedFormats.join(",")}
            className={classes.input}
            id="image-upload"
            type="file"
            onChange={handleChange}
            disabled={uploading}
          />
          <label htmlFor="image-upload">
            <Box>
              <CloudUpload className={classes.uploadIcon} />
              <Typography variant="body1" gutterBottom>
                Arrastra una imagen aquí o haz clic para seleccionar
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Formatos: {acceptedFormats.map((f) => f.split("/")[1]).join(", ")}
                {" | "}
                Tamaño máximo: {maxSizeMB}MB
              </Typography>
            </Box>
          </label>
          {uploading && (
            <Box className={classes.progress}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="caption" color="textSecondary">
                Subiendo... {progress}%
              </Typography>
            </Box>
          )}
        </Paper>
      ) : (
        <Box className={classes.preview}>
          <img
            src={preview}
            alt="Preview"
            className={classes.previewImage}
          />
          <IconButton
            className={classes.deleteButton}
            size="small"
            onClick={handleRemove}
          >
            <Delete />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default ImageUploader;
